/**
 * Carry a started highlight from the chat to the map.
 *
 * "Out of the chat" is one of the three ways a highlight comes into being:
 * the paragraph is already written, and now it should also stand as a point
 * on the map. The text has to get from one page to the other.
 *
 * **Through `sessionStorage`, not through the address.** A message text does
 * not belong in a URL: it would sit in the browser history, in every bookmark
 * and in every screenshot of the address bar. `sessionStorage` ends with the
 * window and goes nowhere.
 *
 * Picked up **once**: the map takes the hand-over and deletes it. Otherwise
 * the same form would open again the next time the map is opened.
 *
 * **Since 1.16.0 the photos and videos of the post travel along** (from use:
 * "take the data (time, place) and the picture itself into the highlight").
 * What is stored here is what the chat knows about them -- id, kind, the
 * addresses of the thumbnails. Where and when they were taken is *not* in the
 * chat's message list (it would go out to every reader), so the map asks the
 * trip's own media list for it once it has picked the hand-over up.
 */
import type { Media } from '../api/types'

const SCHLUESSEL = 'tb.highlight.uebergabe'

export interface Uebergabe {
  tripId: number
  title: string
  body: string
  /** When the message was written -- the first hint for the time. */
  at: string | null
  /** The post's photos and videos, in the order of the post. Never audio. */
  media: Media[]
}

export function merkeUebergabe(uebergabe: Uebergabe): void {
  try {
    sessionStorage.setItem(SCHLUESSEL, JSON.stringify(uebergabe))
  } catch {
    // Private mode, full storage: then simply without prefill.
  }
}

/** Picks up the hand-over for this trip and clears it. */
export function holUebergabe(tripId: number): Uebergabe | null {
  try {
    const roh = sessionStorage.getItem(SCHLUESSEL)
    if (roh === null) return null
    sessionStorage.removeItem(SCHLUESSEL)

    const gelesen = JSON.parse(roh) as Partial<Uebergabe>
    // Meant for another trip: do not apply. This is no security lock, it
    // prevents a confusing form.
    if (gelesen.tripId !== tripId) return null

    return {
      tripId,
      title: typeof gelesen.title === 'string' ? gelesen.title : '',
      body: typeof gelesen.body === 'string' ? gelesen.body : '',
      at: typeof gelesen.at === 'string' ? gelesen.at : null,
      // A hand-over written before 1.16.0 has no list; an odd entry is not
      // a picture the form could show or the server could attach.
      media: Array.isArray(gelesen.media)
        ? gelesen.media.filter(
            (m): m is Media =>
              typeof m === 'object' && m !== null && Number.isFinite((m as Media).id)
              && ((m as Media).kind === 'image' || (m as Media).kind === 'video'),
          )
        : [],
    }
  } catch {
    return null
  }
}
